export const CHART_SAMPLING_MODES = {
  DEFAULT: 'default',
  POINTS_PER_HOUR: 'pointsPerHour',
};

export const DEFAULT_POINTS_PER_HOUR = 60;
export const MIN_POINTS_PER_HOUR = 1;
export const MAX_POINTS_PER_HOUR = 3600;
export const CHART_SAMPLING_RANGES = [
  { key: 'upTo1Hour', label: '1 hour or less', maxSeconds: 3600 },
  { key: 'upTo1Day', label: 'More than 1 hour to 1 day', maxSeconds: 86400 },
  { key: 'upTo1Week', label: 'More than 1 day to 1 week', maxSeconds: 604800 },
  { key: 'upTo1Month', label: 'More than 1 week to 1 month', maxSeconds: 2592000 },
  { key: 'upTo3Months', label: 'More than 1 month to 3 months', maxSeconds: 7776000 },
  { key: 'upTo6Months', label: 'More than 3 to 6 months', maxSeconds: 15552000 },
  { key: 'over6Months', label: 'More than 6 months', maxSeconds: Infinity },
];
export const normalizePointsPerHourByRange = (value = {}, fallback = DEFAULT_POINTS_PER_HOUR) => (
  Object.fromEntries(CHART_SAMPLING_RANGES.map(({ key }) => [key, normalizePointsPerHour(value?.[key], fallback)]))
);

export const normalizeChartSamplingMode = (value) => (
  String(value || '').trim() === CHART_SAMPLING_MODES.POINTS_PER_HOUR
    ? CHART_SAMPLING_MODES.POINTS_PER_HOUR
    : CHART_SAMPLING_MODES.DEFAULT
);

export const normalizePointsPerHour = (value, fallback = DEFAULT_POINTS_PER_HOUR) => {
  const numericValue = Number(value);
  const normalizedFallback = Number.isFinite(Number(fallback))
    ? Number(fallback)
    : DEFAULT_POINTS_PER_HOUR;
  const safeValue = Number.isFinite(numericValue) ? numericValue : normalizedFallback;

  return Math.min(
    MAX_POINTS_PER_HOUR,
    Math.max(MIN_POINTS_PER_HOUR, Math.round(safeValue))
  );
};

export const normalizeChartSamplingLegacyMode = (value) => (
  String(value || '').trim().toLowerCase() === 'dashboard' ? 'dashboard' : 'device'
);

/**
 * Build the sampling instruction sent to the API.
 * The frontend only sends the user setting. The backend calculates the real
 * aggregate window from tstart/tstop so all charts stay consistent and safe.
 */
export const buildChartSamplingPayload = ({ props = {}, legacyMode = 'device' } = {}) => {
  const mode = normalizeChartSamplingMode(props?.samplingMode);
  const payload = {
    mode,
    legacyMode: normalizeChartSamplingLegacyMode(legacyMode),
  };

  if (mode === CHART_SAMPLING_MODES.POINTS_PER_HOUR) {
    // Omit unset values so the API can apply the super-user defaults for the
    // matching period range. Existing widgets with explicit values keep them.
    if (props?.pointsPerHour != null) {
      payload.pointsPerHour = normalizePointsPerHour(props.pointsPerHour);
    }
    if (props?.pointsPerHourByRange && typeof props.pointsPerHourByRange === 'object') {
      payload.pointsPerHourByRange = Object.fromEntries(
        CHART_SAMPLING_RANGES
          .filter(({ key }) => props.pointsPerHourByRange[key] != null)
          .map(({ key }) => [
            key,
            normalizePointsPerHour(props.pointsPerHourByRange[key], payload.pointsPerHour),
          ])
      );
    }
  }

  return payload;
};

export const getChartSamplingGroupKey = ({ props = {}, legacyMode = 'device' } = {}) => {
  const payload = buildChartSamplingPayload({ props, legacyMode });
  return `${payload.legacyMode}:${payload.mode}:${payload.pointsPerHour || ''}:${JSON.stringify(payload.pointsPerHourByRange || {})}`;
};

// Kept only for backward-compatible imports from older files. New requests should
// send buildChartSamplingPayload(...) and let the backend calculate the window.
const normalizeRangeSeconds = (rangeSeconds) => {
  const numericRange = Number(rangeSeconds);
  return Number.isFinite(numericRange) && numericRange > 0 ? numericRange : 0;
};

const getLegacyDeviceTwinSeconds = (rangeSeconds) => {
  const normalizedRangeSeconds = normalizeRangeSeconds(rangeSeconds);
  const timeDiffHours = normalizedRangeSeconds / 3600;
  const legacyTwin = Math.round(timeDiffHours * 12);

  return Math.max(1, legacyTwin * 3);
};

const getLegacyDashboardTwinSeconds = (rangeSeconds) => {
  const normalizedRangeSeconds = normalizeRangeSeconds(rangeSeconds);
  const timeDiffHours = normalizedRangeSeconds / 3600;
  const legacyTwin = Math.round(timeDiffHours * 12);

  return Math.max(10, legacyTwin);
};

export const getPointsPerHourWindowSeconds = ({ rangeSeconds, pointsPerHour }) => {
  const normalizedRangeSeconds = normalizeRangeSeconds(rangeSeconds);
  if (!normalizedRangeSeconds) return 1;

  const normalizedPointsPerHour = normalizePointsPerHour(pointsPerHour);
  const rangeHours = normalizedRangeSeconds / 3600;
  const totalPoints = Math.max(1, Math.round(rangeHours * normalizedPointsPerHour));

  return Math.max(1, Math.round(normalizedRangeSeconds / totalPoints));
};

export const getChartQueryTwin = ({ rangeSeconds, props = {}, legacyMode = 'device' }) => {
  const samplingMode = normalizeChartSamplingMode(props?.samplingMode);

  if (samplingMode === CHART_SAMPLING_MODES.POINTS_PER_HOUR) {
    return String(getPointsPerHourWindowSeconds({
      rangeSeconds,
      pointsPerHour: props?.pointsPerHour,
    }));
  }

  return String(
    normalizeChartSamplingLegacyMode(legacyMode) === 'dashboard'
      ? getLegacyDashboardTwinSeconds(rangeSeconds)
      : getLegacyDeviceTwinSeconds(rangeSeconds)
  );
};
